import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  normalizedPreflightTenantId,
  preflightKeys,
  preflightListKeyParams,
} from "@/lib/queries";
import { getLocalUserFacingApiMessage } from "@/lib/api-client";
import { createReport } from "../api/preflight.api";
import {
  bumpMarketReportCount,
  bumpTenantReportCount,
  invalidateMarketAggregates,
} from "../lib/market-cache";
import { listScopeIncludesMarkets } from "../lib/market-filter";
import { usePreflightDraftStore } from "../stores/preflight-draft.store";
import type {
  CreateReportInput,
  CreateReportResult,
  PreflightMarket,
  PreflightMarketsList,
  PreflightReportListPage,
  PreflightReportSummary,
} from "../types/preflight.types";

type ListData = InfiniteData<PreflightReportListPage, string | undefined>;

interface PendingRowInput {
  reportId: string;
  input: CreateReportInput;
  pendingRowName: string;
  markets: PreflightMarket[];
}

function buildPendingRow({
  reportId,
  input,
  pendingRowName,
  markets,
}: PendingRowInput): PreflightReportSummary {
  const now = new Date().toISOString();
  return {
    id: reportId,
    name: input.name?.trim() || pendingRowName,
    brandName: input.brandName,
    platform: input.platform,
    placement: input.placement,
    status: "QUEUED",
    overallScore: null,
    overallBand: null,
    analyzerVersion: null,
    assetCount: input.uploadIds.length + input.urls.length,
    scoredCount: 0,
    failedCount: 0,
    criteriaPassedCount: 0,
    criteriaFailedCount: 0,
    createdBy: null,
    markets,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * The market the report was created in, read from the cached market list the create
 * form selects from. Null until that list is cached — the row then shows the same
 * placeholder a legacy report does, and reconciles on the first list fetch.
 */
function getCachedMarkets(
  queryClient: QueryClient,
  tenantId: string,
  marketIds: string[],
): PreflightMarket[] {
  const cached = queryClient.getQueryData<PreflightMarketsList>(
    preflightKeys.markets(tenantId),
  );
  return marketIds.flatMap((id) => {
    const market = cached?.markets.find((row) => row.id === id);
    return market
      ? [{ id: market.id, kind: market.kind, name: market.name, code: market.code }]
      : [];
  });
}

/** Whether a cached list's market scope covers the new report (all-markets, or any submitted id). */
function isRowInListScope(key: QueryKey, coveringMarketIds: readonly string[]): boolean {
  return listScopeIncludesMarkets(
    preflightListKeyParams(key)?.market,
    coveringMarketIds,
  );
}

/**
 * Prepend the synthetic pending row into the first page of every cached reports
 * list whose market scope covers it, so it appears immediately without surfacing
 * in a differently scoped table. Reconciled by `reportId` on the first successful
 * list/detail fetch (poll → invalidate).
 */
function prependPendingRow(
  queryClient: QueryClient,
  tenantId: string,
  row: PreflightReportSummary,
  coveringMarketIds: readonly string[],
): void {
  const covering = {
    queryKey: preflightKeys.listsForTenant(tenantId),
    predicate: (query: { queryKey: QueryKey }) =>
      isRowInListScope(query.queryKey, coveringMarketIds),
  };
  const entries = queryClient.getQueriesData<ListData>(covering);
  let prepended = false;
  for (const [key, data] of entries) {
    if (!data || data.pages.length === 0) continue;
    const [firstPage, ...rest] = data.pages;
    queryClient.setQueryData<ListData>(key, {
      ...data,
      pages: [
        { ...firstPage!, items: [row, ...firstPage!.items] },
        ...rest,
      ],
    });
    prepended = true;
  }
  if (!prepended) {
    void queryClient.invalidateQueries(covering);
  }
}

/**
 * Create-report mutation (KTD2 generate flow). On success: optimistically prepend a
 * QUEUED row, clear the draft, and toast — the user stays on the Hub and the new row
 * begins polling (U6). On failure: the draft is preserved and an error toast shown.
 * `createReport` returns only `{ reportId }`, so the row is seeded as QUEUED.
 */
export function useCreateReport() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  return useMutation<CreateReportResult, Error, CreateReportInput>({
    mutationFn: (input) => createReport(input),
    onSuccess: (result, input) => {
      const tenantId = normalizedPreflightTenantId(activeTenantId);
      prependPendingRow(
        queryClient,
        tenantId,
        buildPendingRow({
          reportId: result.reportId,
          input,
          pendingRowName: t("preflight.pendingRowName"),
          markets: getCachedMarkets(queryClient, tenantId, input.marketIds),
        }),
        input.marketIds,
      );
      // Hub empty states key off market report counts — bump immediately so the
      // first report exits the no-reports state before the markets refetch lands.
      bumpTenantReportCount(queryClient, tenantId, 1);
      for (const marketId of input.marketIds) {
        bumpMarketReportCount(queryClient, tenantId, marketId, 1);
      }
      invalidateMarketAggregates(queryClient, tenantId);
      usePreflightDraftStore.getState().clear();
      toast.success(t("preflight.toast.analysisStarted"));
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
      // Publish can fail after the report row is already committed as FAILED.
      // Refetch counts and hub lists so a persisted live report is not hidden.
      const tenantId = normalizedPreflightTenantId(activeTenantId);
      invalidateMarketAggregates(queryClient, tenantId);
      void queryClient.invalidateQueries({
        queryKey: preflightKeys.listsForTenant(tenantId),
      });
    },
    meta: { skipGlobalError: true },
  });
}
